import React, { useContext } from 'react';

import { Select } from '@aline/common';
import { Box, IconButton } from '@mui/material';
import { CircleCheck, CircleX, Undo2 } from 'lucide-react';
import { AlineTooltip } from 'src/components';
import type {
  OrphanRelationshipRow,
  ReportGridRow,
  UrlRelationshipRow,
} from 'src/components/reports/types';
import { Apollo } from 'src/gql/generated';
import { useRelationshipActions } from 'src/hooks';

import { DocumentGridReportIdContext } from './RepositoryContextProviders';
import { AIReasoningTooltip, useRelationship } from './repositoryDocumentNameCellHelpers';

interface RelationshipActionButtonsProps {
  row: OrphanRelationshipRow | ReportGridRow | UrlRelationshipRow;
  confirmButtonCy?: string;
  confirmTooltip?: string;
  rejectButtonCy?: string;
  rejectTooltip?: string;
}

/**
 * Orphan rows return nothing: their cell already renders the reasoning tooltip
 * next to the relationship label, so repeating it here would show two icons.
 */
function getRowLlmReasonsMarkdown(
  row: RelationshipActionButtonsProps['row']
): string | null | undefined {
  switch (row.__typename) {
    case 'Document':
      return row.relationship?.llmReasonsMarkdown;
    case 'UrlRelationshipRow':
      return row.urlRelationship?.llmReasonsMarkdown;
    case 'OrphanRelationshipRow':
      return undefined;
  }
}

export const RelationshipActionButtons = React.memo(function RelationshipActionButtons({
  confirmButtonCy = Select.relationships.confirmButton,
  confirmTooltip = 'Accept relationship',
  rejectButtonCy = Select.relationships.rejectButton,
  rejectTooltip = 'Reject relationship',
  row,
}: RelationshipActionButtonsProps): JSX.Element | null {
  const { relationship } = useRelationship(row);
  const repositoryId = useContext(DocumentGridReportIdContext);
  const {
    confirming,
    handleConfirmRelationship,
    handleRejectRelationship,
    handleUndoRelationship,
    rejecting,
    undoing,
  } = useRelationshipActions({
    repositoryId,
    row,
  });
  const review = relationship?.review;
  const isMutating = confirming || rejecting || undoing;
  const reviewedByName = review?.reviewedBy?.displayName ?? null;
  const llmReasonsMarkdown = getRowLlmReasonsMarkdown(row);

  if (!review) {
    return (
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
        }}
      >
        {typeof llmReasonsMarkdown === 'string' && (
          <AIReasoningTooltip
            llmReasonsMarkdown={llmReasonsMarkdown}
            relationshipId={relationship?.id}
          />
        )}
        <AlineTooltip arrow placement="top" title={confirmTooltip}>
          <span data-cy={confirmButtonCy}>
            <IconButton
              disabled={!relationship?.childRelationshipType || isMutating}
              size="medium"
              onClick={async event => {
                event.stopPropagation();
                await handleConfirmRelationship();
              }}
            >
              <CircleCheck size={18} />
            </IconButton>
          </span>
        </AlineTooltip>
        <AlineTooltip arrow placement="top" title={rejectTooltip}>
          <span data-cy={rejectButtonCy}>
            <IconButton
              disabled={isMutating}
              size="medium"
              onClick={async event => {
                event.stopPropagation();
                await handleRejectRelationship();
              }}
            >
              <CircleX size={18} />
            </IconButton>
          </span>
        </AlineTooltip>
      </Box>
    );
  }

  switch (review.outcome) {
    case Apollo.ReviewOutcome.Confirmed: {
      const undoTooltip = reviewedByName
        ? `Accepted by ${reviewedByName}. Click to undo.`
        : 'Accepted. Click to undo.';

      return (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
          }}
        >
          {typeof llmReasonsMarkdown === 'string' && (
            <AIReasoningTooltip
              llmReasonsMarkdown={llmReasonsMarkdown}
              relationshipId={relationship?.id}
            />
          )}
          <AlineTooltip arrow placement="top" title={undoTooltip}>
            <span data-cy={confirmButtonCy}>
              <IconButton
                disabled={isMutating}
                size="medium"
                onClick={async event => {
                  event.stopPropagation();
                  await handleUndoRelationship();
                }}
              >
                <Undo2 size={16} />
              </IconButton>
            </span>
          </AlineTooltip>
        </Box>
      );
    }
    case Apollo.ReviewOutcome.Rejected: {
      const undoTooltip = reviewedByName
        ? `Rejected by ${reviewedByName}. Click to undo.`
        : 'Rejected. Click to undo.';

      return (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
          }}
        >
          {typeof llmReasonsMarkdown === 'string' && (
            <AIReasoningTooltip
              llmReasonsMarkdown={llmReasonsMarkdown}
              relationshipId={relationship?.id}
            />
          )}
          <AlineTooltip arrow placement="top" title={undoTooltip}>
            <span data-cy={rejectButtonCy}>
              <IconButton
                disabled={isMutating}
                size="medium"
                onClick={async event => {
                  event.stopPropagation();
                  await handleUndoRelationship();
                }}
              >
                <Undo2 size={16} />
              </IconButton>
            </span>
          </AlineTooltip>
        </Box>
      );
    }
  }
});
